import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { CheckCircle, Loader2 } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { useData } from '../hooks/useData'
import LoadingSpinner from '../components/LoadingSpinner'
import { AreaChoice, QuestionBlock, TeamBanner } from '../components/surveys/SurveyInputs'
import type { SurveyAnswer, SurveyForm, SurveyQuestion } from '../types'

/**
 * Public page a survey round's email links to: /feedback/:roundId#token.
 *
 * The token in the link is the person's credential and the server's one-answer
 * check, and it is all the page sends back with the answers - never a name or
 * an email. It sits after "#", which the browser never sends to a server, and
 * the page passes it to the API in the request body, so no log records it. The server records the answers against the department the link was
 * made for, which the page has no say in. Someone in a department split into
 * areas (CCD) first chooses the one area they give feedback on.
 */
export default function Feedback() {
  const api = useApi()
  const { roundId } = useParams<{ roundId: string }>()
  const token = useLocation().hash.replace(/^#/, '')
  const { data: form, loading } = useData<SurveyForm | null>(
    () => api.getSurveyForm(roundId!, token!),
    [roundId, token],
  )
  const [answers, setAnswers] = useState<Record<string, SurveyAnswer>>({})
  const [area, setArea] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [outcome, setOutcome] = useState<'thanks' | 'already' | 'closed' | null>(null)
  const [error, setError] = useState('')

  // The app's CSS pins html/body/#root to the viewport for the sidebar layout.
  // This page stands alone, so it scrolls normally while mounted.
  useEffect(() => {
    const root = document.getElementById('root')
    const targets = [document.documentElement, document.body, root].filter(Boolean) as HTMLElement[]
    const before = targets.map(el => [el.style.overflow, el.style.height])
    targets.forEach(el => { el.style.overflow = 'auto'; el.style.height = 'auto' })
    return () => targets.forEach((el, i) => { el.style.overflow = before[i][0]; el.style.height = before[i][1] })
  }, [])

  const set = (id: string, value: SurveyAnswer) => setAnswers(prev => ({ ...prev, [id]: value }))
  const answered = (q: SurveyQuestion) => {
    const v = answers[q.id]
    return v !== undefined && v !== null && (typeof v !== 'string' || v.trim() !== '')
  }
  const areas = form?.areas ?? []
  const chosen = areas.find(a => a.id === area)
  // In a split department the questions wait for an area, whose team they name.
  const questions = !form || (areas.length && !chosen) ? [] : form.questions
  const partner = chosen ?? (form ? { team: form.team, supports: form.supports, partner: form.partner } : undefined)
  const missing = questions.filter(q => q.required && !answered(q))
  const ready = (!areas.length || !!chosen) && !missing.length

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form || !ready) return
    setSubmitting(true)
    setError('')
    try {
      await api.submitSurveyAnswers(roundId!, token!, answers, chosen?.id)
      setOutcome('thanks')
    } catch (err: any) {
      if (err?.status === 409) setOutcome('already')
      else if (err?.status === 410) setOutcome('closed')
      else setError(err?.message || 'Your answers could not be sent. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) return <LoadingSpinner fullScreen />

  if (!form) return <Message title="Survey Not Found" text="This survey link is invalid." />
  if (outcome === 'thanks') return <Message title="Thank You" text="Your answers have been sent." done />
  if (outcome === 'already' || (form.submitted && !outcome)) {
    return <Message title="Already Answered" text="This link has already been used to answer the survey." />
  }
  if (outcome === 'closed' || form.status !== 'open') {
    return <Message title="Survey Closed" text="This survey is no longer taking answers." />
  }

  return (
    <div className="min-h-screen bg-[#F0F9FF] flex items-center justify-center p-8">
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="bg-[#005EB8] px-8 py-6">
            <img src="/InvertedLogo.png" alt="Danone" className="h-10 mb-4" />
            <h1 className="text-2xl font-bold text-white">{form.typeName}</h1>
            <p className="text-white/80 text-sm mt-1">
              {form.roundName}{areas.length ? ` · ${chosen?.name ?? form.department}` : !form.team ? ` · ${form.partner}` : ''}
            </p>
            {!areas.length && partner?.team && <TeamBanner team={partner.team} supports={partner.supports ?? []} mine={form.department} />}
          </div>

          <form onSubmit={submit} className="p-8">
            <p className="text-slate-500 text-sm mb-6">Your answers are anonymous; only your department is recorded.</p>

            {areas.length > 0 && <AreaChoice areas={areas} value={area} onChange={setArea} />}

            {questions.map((q, i) => (
              <QuestionBlock key={q.id} question={q} index={i} partner={partner} value={answers[q.id]} onChange={v => set(q.id, v)} />
            ))}

            {error && <p className="text-sm text-[#EF3340] mb-4">{error}</p>}

            <button type="submit" disabled={submitting || !ready}
              className="w-full btn-primary text-white py-3.5 rounded-xl font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? 'Sending...' : 'Send Answers'}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

function Message({ title, text, done }: { title: string; text: string; done?: boolean }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="card p-8 text-center max-w-md">
        {done && (
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        )}
        <h1 className="text-xl font-bold text-slate-800 mb-2">{title}</h1>
        <p className="text-slate-500">{text}</p>
      </div>
    </div>
  )
}
